import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSchool } from '@/contexts/SchoolContext';
import { workflowEngineService } from '@/services/workflow/WorkflowEngineService';

const CACHE_PREFIX = 'swan_cached_workflow_instances_';
const readCache = (schoolId) => {
  if (!schoolId) return null;
  try { return JSON.parse(sessionStorage.getItem(CACHE_PREFIX + schoolId)) ?? null; }
  catch { return null; }
};
const writeCache = (schoolId, value) => {
  if (!schoolId) return;
  try { sessionStorage.setItem(CACHE_PREFIX + schoolId, JSON.stringify(value)); }
  catch { /* quota */ }
};

/**
 * useWorkflowInstances - Fetches and manages workflow instances for the current school.
 */
export function useWorkflowInstances() {
  const { schoolId } = useSchool();

  const [instances, setInstances] = useState(() => readCache(schoolId) || []);
  const [loading, setLoading] = useState(() => readCache(schoolId) == null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active'); // 'active' | 'completed' | 'cancelled' | 'all'

  const fetchInstances = useCallback(async (force = false) => {
    if (!schoolId) {
      setInstances([]);
      setLoading(false);
      return;
    }

    setError(null);

    try {
      const result = await workflowEngineService.getInstances(schoolId, force);
      setInstances(result);
      writeCache(schoolId, result);
    } catch (err) {
      console.error('[useWorkflowInstances] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    const cached = readCache(schoolId);
    if (cached != null) {
      setInstances(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    fetchInstances();
  }, [fetchInstances, schoolId]);

  const refetch = useCallback(() => fetchInstances(true), [fetchInstances]);

  const filteredInstances = useMemo(() => {
    if (filter === 'all') return instances;
    return instances.filter(i => i.status === filter);
  }, [instances, filter]);

  const stats = useMemo(() => ({
    total: instances.length,
    active: instances.filter(i => i.status === 'active').length,
    completed: instances.filter(i => i.status === 'completed').length,
    cancelled: instances.filter(i => i.status === 'cancelled').length,
  }), [instances]);

  const getInstance = useCallback(async (instanceId) => {
    if (!schoolId) return null;
    return workflowEngineService.getInstance(schoolId, instanceId);
  }, [schoolId]);

  return {
    instances: filteredInstances,
    allInstances: instances,
    loading,
    error,
    filter,
    setFilter,
    stats,
    getInstance,
    refetch,
  };
}

export default useWorkflowInstances;
